import { auth } from '../store/auth.js';

const BASE = '/api';
let refreshing = null;

async function doFetch(path, options = {}) {
  const opts = { ...options };
  opts.headers = { ...(opts.headers || {}) };
  if (!(opts.body instanceof FormData) && opts.body && typeof opts.body !== 'string') {
    opts.body = JSON.stringify(opts.body);
    opts.headers['Content-Type'] = 'application/json';
  }
  if (auth.access) {
    opts.headers['Authorization'] = `Bearer ${auth.access}`;
  }
  const res = await fetch(BASE + path, opts);
  return res;
}

async function tryRefresh() {
  if (!auth.refresh) return false;
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const res = await fetch(BASE + '/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: auth.refresh }),
      });
      if (!res.ok) { auth.clear(); return false; }
      const data = await res.json();
      auth.set(data);
      return true;
    } catch { return false; }
    finally { refreshing = null; }
  })();
  return refreshing;
}

// Sesión vencida sin forma de renovarla: quien entró desde el Hub vuelve a
// entrar por el Hub (sin pedir clave si allá sigue abierta); los demás, al login.
function sesionVencida() {
  let viaHub = false;
  try { viaHub = (localStorage.getItem('inventario.auth') || '').includes('hub-sso'); } catch { /* sin almacenamiento */ }
  auth.clear();
  if (viaHub) {
    const hub = 'https://hub.' + (location.hostname.split('.').slice(-2).join('.') || 'niopixel.com');
    window.location.href = `${hub}/launch/inventario`;
  } else {
    window.location.hash = '#/login';
  }
  throw new ApiError(401, 'Sesion expirada', null);
}

export async function api(method, path, body, opts = {}) {
  let res = await doFetch(path, { method, body, ...opts });
  if (res.status === 401 && !path.startsWith('/auth/login')) {
    const ok = auth.refresh ? await tryRefresh() : false;
    if (!ok) sesionVencida();
    res = await doFetch(path, { method, body, ...opts });
    if (res.status === 401) sesionVencida();
  }
  let data = null;
  if (res.status !== 204) {
    const ct = res.headers.get('content-type') || '';
    data = ct.includes('json') ? await res.json() : await res.text();
  }
  if (!res.ok) {
    throw new ApiError(res.status, (data && data.error) || res.statusText, data);
  }
  return data;
}

export class ApiError extends Error {
  constructor(status, message, body) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export const get  = (p, q) => api('GET',    p + buildQuery(q));
export const post = (p, b) => api('POST',   p, b);
export const put  = (p, b) => api('PUT',    p, b);
export const del  = (p)    => api('DELETE', p);

export function postFormData(p, formData) {
  return api('POST', p, formData);
}

/**
 * Descarga un blob autenticado (PDF/CSV/XLSX) y dispara el "save as" del navegador.
 */
export async function downloadFile(path, suggestedName) {
  let res = await doFetch(path, { method: 'GET' });
  if (res.status === 401 && auth.refresh) {
    if (await tryRefresh()) res = await doFetch(path, { method: 'GET' });
  }
  if (!res.ok) throw new ApiError(res.status, `Descarga fallo (${res.status})`, null);
  const blob = await res.blob();
  let filename = suggestedName;
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  if (m) filename = m[1];
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'download';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Abre un blob autenticado en una nueva pestana (PDFs).
 */
export async function openFile(path) {
  let res = await doFetch(path, { method: 'GET' });
  if (res.status === 401 && auth.refresh) {
    if (await tryRefresh()) res = await doFetch(path, { method: 'GET' });
  }
  if (!res.ok) throw new ApiError(res.status, 'No se pudo abrir el archivo', null);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function buildQuery(q) {
  if (!q) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v == null || v === '') continue;
    params.set(k, v);
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}
